import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FrameworkAgreementDto, useOpenMiniCompetitionMutation } from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { isForbiddenError } from "../../common/errors/isNoAccessError";
import { isApiErrorDto } from "../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../common/errors/isFetchBaseQueryError";
import { urls } from "../../urls";

export const NewMiniCompetitionModal = ({
  framework,
  isOpen,
  onClose,
}: {
  framework: FrameworkAgreementDto;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const [open, { isLoading }] = useOpenMiniCompetitionMutation();
  const [title, setTitle] = useState("");
  const [refused, setRefused] = useState(false);
  const submitting = useRef(false);

  const submit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      const created = await open({
        frameworkAgreementId: framework.id,
        openMiniCompetitionRequest: { title: title.trim() },
      }).unwrap();
      onClose();
      const details = urls.app.sourcingEvents.edit.go(created.id).details;
      navigate({ pathname: details.pathname, search: details.search });
    } catch (e) {
      if (isForbiddenError(e) && !hasErrorCode(e)) {
        setRefused(true);
      } else {
        displayer.trigger(e);
      }
    } finally {
      submitting.current = false;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="smModalBackground">
        <ModalHeader>{t("New mini-competition")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel htmlFor="mini-competition-title">{t("What are you buying?")}</FormLabel>
            <Input
              id="mini-competition-title"
              backgroundColor="smBackground"
              autoFocus
              placeholder={t("For example: Advisory services Q1 2027") ?? ""}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && title.trim() !== "") submit();
              }}
            />
            <FormHelperText>
              {t("This is the name the suppliers on the panel will see.")}
            </FormHelperText>
          </FormControl>
          <Text mt="4" fontSize="sm" color="smMuted">
            {t("{{count}} suppliers on the panel are invited.", { count: framework.summary.supplierCount })}
          </Text>
          {refused && (
            <Alert status="warning" rounded="md" mt="4" fontSize="sm" alignItems="flex-start">
              <AlertIcon />
              {t(
                "You don't have access to start a mini-competition on this framework agreement. Ask an administrator in your organization for access.",
              )}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" isLoading={isLoading} isDisabled={title.trim() === "" || refused} onClick={submit}>
            {t("Create mini-competition")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const hasErrorCode = (error: unknown): boolean =>
  isFetchBaseQueryError(error) && isApiErrorDto(error.data) && !!error.data.errorCode;
